import React, { useState, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import ViewerNavbar from "./ViewerNavbar";
import API from "../../services/api.ts";
import { getMediaUrl } from "../../services/media.ts";
import "./DiscoveryViewerTest.css";

const DiscoveryViewerTest = () => {
    // Get parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    const studyIdParam = urlParams.get('study_id');
    const patientIdParam = urlParams.get('patient_id');
    const imageIdsParam = urlParams.get('image_ids');
    const imageIdParam = urlParams.get('image_id'); // Keep for backward compatibility
    const imageDataParam = urlParams.get('image_data'); // New format: "id:source_model,id:source_model"
    const documentIdParam = urlParams.get('document_id'); // Legacy single document
    const documentSourceParam = urlParams.get('document_source'); // Legacy: "MedicalDocument" or "PatientData"
    const documentDataParam = urlParams.get('document_data'); // New format: "id:source_model,id:source_model"

    const [studyId] = useState(studyIdParam || localStorage.getItem('studyId'));
    const [patientId] = useState(patientIdParam || 'Unknown');
    const [studyName, setStudyName] = useState("");
    const [labels, setLabels] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [scores, setScores] = useState([]);
    const [shareAnnotations, setShareAnnotations] = useState(false);
    const [imagesData, setImagesData] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [documentsData, setDocumentsData] = useState([]);
    const [documentsContent, setDocumentsContent] = useState({});
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    // Fetch study info including labels, questions, and scores
    const fetchStudyInfo = useCallback(async () => {
        if (!studyId) return;

        try {
            const response = await API.get(`/studies/${studyId}/`);
            setStudyName(response.data.name);
            setLabels(response.data.labels || []);
            setQuestions(response.data.questions || []);
            setScores(response.data.scores || []);
            setShareAnnotations(response.data.share_annotations || false);
        } catch (err) {
            console.error("Error fetching study info:", err);
        }
    }, [studyId]);

    // Fetch images data (supports multiple images from different sources)
    const fetchImagesData = useCallback(async () => {
        // Parse image data - support new format (image_data with source), old format (image_ids), and legacy (image_id)
        let imageItems = [];

        if (imageDataParam) {
            // New format: "id:source_model,id:source_model"
            imageItems = imageDataParam.split(',').map(item => {
                const [id, source] = item.split(':');
                return { id: id.trim(), source: source?.trim() || 'MedicalImage' };
            }).filter(item => item.id);
        } else if (imageIdsParam) {
            // Old format: just IDs, assume MedicalImage
            imageItems = imageIdsParam.split(',').map(id => ({
                id: id.trim(),
                source: 'MedicalImage'
            })).filter(item => item.id);
        } else if (imageIdParam) {
            // Legacy single image
            imageItems = [{ id: imageIdParam, source: 'MedicalImage' }];
        }

        // Deduplicate by id+source combination
        const seenItems = new Set();
        imageItems = imageItems.filter(item => {
            const key = `${item.id}:${item.source}`;
            if (seenItems.has(key)) return false;
            seenItems.add(key);
            return true;
        });

        if (imageItems.length === 0) return;

        try {
            const imagesArray = [];

            for (const imageItem of imageItems) {
                try {
                    if (imageItem.source === 'PatientData') {
                        // Fetch from PatientData endpoint
                        const response = await API.get(`/data_manager/patient-data/${imageItem.id}/`);
                        const itemData = response.data;
                        imagesArray.push({
                            ...itemData,
                            filePath: itemData.file,
                            fileName: itemData.filename || 'Image'
                        });
                    } else {
                        // Fetch from MedicalImage endpoint
                        const imageResponse = await API.get(`/data_manager/medical-images/${imageItem.id}/`);
                        const imgData = imageResponse.data;

                        // Get actual file paths
                        const filesResponse = await API.get(`/data_manager/medical-images/${imageItem.id}/files/`);
                        const filesData = filesResponse.data;

                        if (filesData.files && filesData.files.length > 0) {
                            imagesArray.push({
                                ...imgData,
                                filePath: filesData.files[0].path,
                                fileName: filesData.files[0].name
                            });
                        } else {
                            imagesArray.push({
                                ...imgData,
                                filePath: imgData.image,
                                fileName: imgData.image ? imgData.image.split('/').pop() : 'Unknown'
                            });
                        }
                    }
                } catch (err) {
                    console.error(`Error fetching image ${imageItem.id}:`, err);
                }
            }

            setImagesData(imagesArray);
        } catch (err) {
            console.error("Error fetching images:", err);
        }
    }, [imageDataParam, imageIdsParam, imageIdParam]);

    // Fetch documents data (supports multiple documents from different sources)
    const fetchDocumentsData = useCallback(async () => {
        // Parse document data - support new format (document_data with source) and legacy (document_id)
        let documentItems = [];

        if (documentDataParam) {
            // New format: "id:source_model,id:source_model"
            documentItems = documentDataParam.split(',').map(item => {
                const [id, source] = item.split(':');
                return { id: id.trim(), source: source?.trim() || 'MedicalDocument' };
            }).filter(item => item.id);
        } else if (documentIdParam) {
            // Legacy single document
            documentItems = [{ id: documentIdParam, source: documentSourceParam || 'MedicalDocument' }];
        }

        // Deduplicate by id+source combination
        const seenItems = new Set();
        documentItems = documentItems.filter(item => {
            const key = `${item.id}:${item.source}`;
            if (seenItems.has(key)) return false;
            seenItems.add(key);
            return true;
        });

        if (documentItems.length === 0) return;

        try {
            const docsArray = [];
            const contentsMap = {};
            const textTypes = ['txt', 'md', 'text', 'markdown'];

            for (const docItem of documentItems) {
                try {
                    let docData;

                    if (docItem.source === 'PatientData') {
                        // Fetch from PatientData endpoint
                        const response = await API.get(`/data_manager/patient-data/${docItem.id}/`);
                        docData = response.data;
                        // Map PatientData fields to document-like structure
                        docData.document_type = docData.original_file_type || 'txt';
                        docData.filename = docData.filename || 'Document';
                        docData.source = 'PatientData';
                    } else {
                        // Fetch from MedicalDocument endpoint
                        const docResponse = await API.get(`/data_manager/medical-documents/${docItem.id}/`);
                        docData = docResponse.data;
                        docData.source = 'MedicalDocument';
                    }

                    docsArray.push(docData);

                    // If it's a text or markdown file, fetch its content
                    if (textTypes.includes(docData.document_type?.toLowerCase())) {
                        try {
                            let contentResponse;
                            if (docItem.source === 'PatientData') {
                                contentResponse = await API.get(`/data_manager/patient-data/${docItem.id}/content/`);
                            } else {
                                contentResponse = await API.get(`/data_manager/medical-documents/${docItem.id}/content/`);
                            }
                            if (contentResponse.data && contentResponse.data.content) {
                                contentsMap[docData.id] = contentResponse.data.content;
                            }
                        } catch (err) {
                            console.error("Error fetching document content:", err);
                            contentsMap[docData.id] = "Unable to load document content";
                        }
                    }
                } catch (err) {
                    console.error(`Error fetching document ${docItem.id}:`, err);
                }
            }

            setDocumentsData(docsArray);
            setDocumentsContent(contentsMap);
        } catch (err) {
            console.error("Error fetching documents:", err);
        }
    }, [documentDataParam, documentIdParam, documentSourceParam]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await fetchStudyInfo();
            await fetchImagesData();
            await fetchDocumentsData();
            setLoading(false);
        };
        loadData();
    }, [fetchStudyInfo, fetchImagesData, fetchDocumentsData]);

    // Build image URL from the file path using the media service
    const getImageUrl = (filePath) => {
        if (!filePath) return null;
        return getMediaUrl(filePath);
    };

    const getDocumentIcon = (docType) => {
        const type = docType?.toLowerCase();
        switch (type) {
            case 'pdf':
                return 'fa-file-pdf-o';
            case 'doc':
            case 'docx':
            case 'word':
                return 'fa-file-word-o';
            case 'txt':
            case 'md':
            case 'text':
            case 'markdown':
                return 'fa-file-text-o';
            default:
                return 'fa-file-o';
        }
    };

    // Helper to check if document type is text-based
    const isTextDocument = (docType) => {
        const textTypes = ['txt', 'md', 'text', 'markdown'];
        return textTypes.includes(docType?.toLowerCase());
    };

    // Navigation for multiple images
    const handlePrevImage = () => {
        setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : imagesData.length - 1));
    };

    const handleNextImage = () => {
        setCurrentImageIndex(prev => (prev < imagesData.length - 1 ? prev + 1 : 0));
    };

    // Navigation for multiple documents
    const handlePrevDocument = () => {
        setCurrentDocumentIndex(prev => (prev > 0 ? prev - 1 : documentsData.length - 1));
    };

    const handleNextDocument = () => {
        setCurrentDocumentIndex(prev => (prev < documentsData.length - 1 ? prev + 1 : 0));
    };

    // Get current image data
    const currentImage = imagesData[currentImageIndex] || null;

    // Get current document data and content
    const currentDocument = documentsData[currentDocumentIndex] || null;
    const currentDocumentContent = currentDocument ? documentsContent[currentDocument.id] : null;

    // Build available data types and series for annotation level filtering
    const getAvailableDataTypes = () => {
        const types = [];
        if (imagesData.length > 0) types.push('image');
        if (documentsData.length > 0) types.push('document');
        return types;
    };

    const getAvailableSeries = () => {
        const series = [];
        const seenIds = new Set();

        // Add image series
        imagesData.forEach((image) => {
            const id = `image-${image.id}`;
            if (!seenIds.has(id)) {
                seenIds.add(id);
                series.push({
                    id,
                    name: image.fileName || image.filename || `Image ${image.id}`,
                    dataType: 'image'
                });
            }
        });

        // Add document series
        documentsData.forEach((doc) => {
            const id = `document-${doc.id}`;
            if (!seenIds.has(id)) {
                seenIds.add(id);
                series.push({
                    id,
                    name: doc.filename || doc.title || `Document ${doc.id}`,
                    dataType: 'document'
                });
            }
        });

        return series;
    };

    return (
        <div className="discovery-viewer-page">
            <ViewerNavbar
                studyId={studyId}
                studyName={studyName}
                labels={labels}
                questions={questions}
                scores={scores}
                patientId={patientId}
                shareAnnotations={shareAnnotations}
                availableDataTypes={getAvailableDataTypes()}
                availableSeries={getAvailableSeries()}
            />

            <div className="discovery-viewer-content">
                {loading ? (
                    <div className="discovery-viewer-loading">
                        <div className="spinner"></div>
                        <p>Loading patient data...</p>
                    </div>
                ) : (
                    <>
                        {/* Patient Header */}
                        <div className="patient-header-bar">
                            <div className="patient-info">
                                <i className="fa fa-user-circle"></i>
                                <span className="patient-label">Patient:</span>
                                <span className="patient-id-value">{patientId}</span>
                            </div>
                        </div>

                        {/* Split View Container */}
                        <div className="split-view-container">
                            {/* Left Panel - Image */}
                            <div className="split-panel image-panel">
                                <div className="panel-header">
                                    <i className="fa fa-image"></i>
                                    <span>Medical Image{imagesData.length > 1 ? 's' : ''}</span>
                                    {imagesData.length > 1 && (
                                        <span className="image-counter">
                                            {currentImageIndex + 1} / {imagesData.length}
                                        </span>
                                    )}
                                    {currentImage?.fileName && (
                                        <span className="panel-filename">{currentImage.fileName}</span>
                                    )}
                                </div>
                                <div className="panel-content image-content-wrapper">
                                    {imagesData.length > 1 && (
                                        <button
                                            className="image-nav-btn prev-btn"
                                            onClick={handlePrevImage}
                                            title="Previous image"
                                        >
                                            <i className="fa fa-chevron-left"></i>
                                        </button>
                                    )}
                                    {currentImage?.filePath ? (
                                        <img
                                            src={getImageUrl(currentImage.filePath)}
                                            alt={currentImage.description || currentImage.fileName || 'Medical Image'}
                                            className="viewer-image"
                                        />
                                    ) : (
                                        <div className="no-content">
                                            <i className="fa fa-file-image-o"></i>
                                            <p>No image available</p>
                                        </div>
                                    )}
                                    {imagesData.length > 1 && (
                                        <button
                                            className="image-nav-btn next-btn"
                                            onClick={handleNextImage}
                                            title="Next image"
                                        >
                                            <i className="fa fa-chevron-right"></i>
                                        </button>
                                    )}
                                </div>
                                {currentImage && (
                                    <div className="panel-footer">
                                        {currentImage.modality && (
                                            <span className="meta-badge">{currentImage.modality}</span>
                                        )}
                                        {currentImage.description && (
                                            <span className="meta-description">{currentImage.description}</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Divider */}
                            <div className="split-divider">
                                <div className="divider-line"></div>
                            </div>

                            {/* Right Panel - Document */}
                            <div className="split-panel document-panel">
                                <div className="panel-header">
                                    <i className={`fa ${currentDocument ? getDocumentIcon(currentDocument.document_type) : 'fa-file-text-o'}`}></i>
                                    <span>Medical Document{documentsData.length > 1 ? 's' : ''}</span>
                                    {documentsData.length > 1 && (
                                        <span className="image-counter">
                                            {currentDocumentIndex + 1} / {documentsData.length}
                                        </span>
                                    )}
                                    {currentDocument?.filename && (
                                        <span className="panel-filename">{currentDocument.filename}</span>
                                    )}
                                </div>
                                <div className="panel-content document-content">
                                    {documentsData.length > 1 && (
                                        <button
                                            className="image-nav-btn prev-btn"
                                            onClick={handlePrevDocument}
                                            title="Previous document"
                                        >
                                            <i className="fa fa-chevron-left"></i>
                                        </button>
                                    )}
                                    {currentDocument ? (
                                        isTextDocument(currentDocument.document_type) && currentDocumentContent ? (
                                            <div className="text-document markdown-content">
                                                <ReactMarkdown>{currentDocumentContent}</ReactMarkdown>
                                            </div>
                                        ) : currentDocument.document_type?.toLowerCase() === 'pdf' ? (
                                            <div className="pdf-document">
                                                <iframe
                                                    src={getMediaUrl(currentDocument.file)}
                                                    title="PDF Document"
                                                    className="pdf-iframe"
                                                />
                                            </div>
                                        ) : (
                                            <div className="no-content">
                                                <i className={`fa ${getDocumentIcon(currentDocument.document_type)}`}></i>
                                                <p>Document format ({currentDocument.document_type?.toUpperCase()}) cannot be displayed in viewer</p>
                                            </div>
                                        )
                                    ) : (
                                        <div className="no-content">
                                            <i className="fa fa-file-text-o"></i>
                                            <p>No document available</p>
                                        </div>
                                    )}
                                    {documentsData.length > 1 && (
                                        <button
                                            className="image-nav-btn next-btn"
                                            onClick={handleNextDocument}
                                            title="Next document"
                                        >
                                            <i className="fa fa-chevron-right"></i>
                                        </button>
                                    )}
                                </div>
                                {currentDocument && (
                                    <div className="panel-footer">
                                        <span className="meta-badge">
                                            {currentDocument.document_type?.toUpperCase()}
                                        </span>
                                        {currentDocument.description && (
                                            <span className="meta-description">{currentDocument.description}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DiscoveryViewerTest;
